import { Component, ComponentRef, OnDestroy, OnInit, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { faChevronRight, faUser } from '@fortawesome/free-solid-svg-icons';
import { MenuItem } from 'primeng/api';
import { CustomerInfoDetailsComponent } from './customer-info-details/customer-info-details.component';
import { CustomerInfoFidelityComponent } from './customer-info-fidelity/customer-info-fidelity.component';
import { CustomerInfoProjectsComponent } from './customer-info-projects/customer-info-projects.component';
import { FidelityCardTypeEnum } from '../../services/fidelity/fidelity';
import { CustomerService } from '../../services/customer.service';
import { TunnelDataStore } from '../../stores/tunnel/tunnel-data.store';
import { CustomerState } from '../../stores/tunnel/model/customer-state';
import { Subscribable } from '../../stores/tunnel/model/subscribable';
import { CustomerInfoStore } from '../../stores/customer-info/customer-info.store';
import { CustomerInfoAppointmentComponent } from './customer-info-appointment/customer-info-appointment.component';
import { CalendarEvent } from '../../models/minutpass/full-calendar-model';
import { FeatureSettingsService } from '../../services/feature-settings/feature-settings.service';

enum EMITTER_TYPE {
  ON_DELETE,
  ON_EDIT,
}

interface Emitter {
  name: string;
  type: EMITTER_TYPE;
}

interface CustomMenuItem extends MenuItem {
  component: Type<any>;
  emitters?: Emitter[];
}

@Component({
  selector: 'ng-casto-customer-info',
  templateUrl: './customer-info.component.html',
})
export class CustomerInfoComponent extends Subscribable implements OnInit, OnDestroy {
  @ViewChild('customerInfoContainer', { read: ViewContainerRef, static: true })
  private readonly customerInfoContainer!: ViewContainerRef;
  private componentRef: ComponentRef<any>;

  protected isLoading: boolean = false;
  protected customerInfoVisible: boolean = false;
  protected needCustomerLogout: boolean = false;

  protected baseTabMenuItems: CustomMenuItem[] = [
    {
      id: 'INFO',
      label: 'customer-info.info.title',
      component: CustomerInfoDetailsComponent,
    },
    {
      id: 'FIDELITY',
      label: 'customer-info.fidelity.title',
      component: CustomerInfoFidelityComponent,
    },
    {
      id: 'PROJECTS',
      label: 'customer-info.projects.title',
      component: CustomerInfoProjectsComponent,
    },
  ];

  private readonly appointmentTab: CustomMenuItem = {
    id: 'APPOINTMENT',
    label: 'customer-info.appointment.title',
    component: CustomerInfoAppointmentComponent,
    emitters: [
      {
        name: 'onDelete',
        type: EMITTER_TYPE.ON_DELETE,
      },
      {
        name: 'onEdit',
        type: EMITTER_TYPE.ON_EDIT,
      },
    ],
  };

  protected tabMenuItems: CustomMenuItem[] = this.baseTabMenuItems;

  protected liteTabMenuItems: CustomMenuItem[] = [this.appointmentTab];

  protected appointmentDate: Date = undefined;

  get displayedAppointmentDate(): string {
    return this.appointmentDate?.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  private _activeTabMenuItem: CustomMenuItem;
  get activeTabMenuItem(): CustomMenuItem {
    return this._activeTabMenuItem;
  }

  set activeTabMenuItem(activeTabMenuItem: CustomMenuItem) {
    this._activeTabMenuItem = activeTabMenuItem;
    this.loadComponent(this._activeTabMenuItem);
  }

  get customerFullName() {
    let firstname = '';
    let lastname = '';

    if (this.currentCustomer?.userData?.firstname?.length > 0) {
      const f = this.currentCustomer.userData.firstname;
      firstname = `${f.charAt(0).toUpperCase()}${f.slice(1)} `;
    }

    if (this.currentCustomer?.userData?.lastname?.length > 0) {
      lastname = this.currentCustomer.userData.lastname.toUpperCase();
    }

    return `${firstname}${lastname}`.trim();
  }

  get customerIsLogged() {
    return this.customerService?.userDatas;
  }

  protected currentCustomer: CustomerState;
  protected isLite: boolean = false;

  constructor(
    private readonly tunnelDataStore: TunnelDataStore,
    private readonly customerInfoStore: CustomerInfoStore,
    private readonly customerService: CustomerService,
    private readonly featureSettingsService: FeatureSettingsService
  ) {
    super();
  }

  ngOnInit(): void {
    super.subscribe();
    this.initActiveTabs();
  }

  ngOnDestroy(): void {
    super.unsubscribe();
    this.componentRef?.destroy();
  }

  protected fidelityEnabled(): boolean {
    return this.featureSettingsService.fidelityEnabled;
  }

  protected subscribeToObservables() {
    this.subscriptions.push(
      this.tunnelDataStore.getCurrentCustomer().subscribe(customer => {
        if (customer) {
          this.currentCustomer = customer;
          this.removeFidelityTabIfNeeded();
        }
      }),
      this.tunnelDataStore.getFidelityLoading().subscribe(async fidelityLoading => {
        this.isLoading = fidelityLoading;
      }),
      this.customerInfoStore?.getIsOpen().subscribe(isOpen => {
        if (isOpen !== undefined) {
          this.customerInfoVisible = isOpen;
        }
      }),
      this.customerInfoStore?.getAppointment().subscribe(appointment => {
        this.toggleAppointmentTab(appointment);
        this.initActiveTabs();
      }),
      this.customerInfoStore?.getNeedLogoutOnClose().subscribe(needLogoutOnClose => {
        this.needCustomerLogout = needLogoutOnClose;
      }),
      this.customerInfoStore?.getIsLite().subscribe(isLite => {
        if (isLite) {
          this.enableLiteMode();
        } else {
          this.disableLiteMode();
        }
      })
    );
  }

  private enableLiteMode() {
    this.tabMenuItems = this.liteTabMenuItems;
    this.activeTabMenuItem = this.tabMenuItems[0];
  }

  private disableLiteMode() {
    if (this.tabMenuItems !== this.liteTabMenuItems) return;
    this.tabMenuItems = this.baseTabMenuItems;
    this.activeTabMenuItem = this.tabMenuItems[0];
  }

  private initActiveTabs() {
    this.activeTabMenuItem = this.tabMenuItems[0];
  }

  private removeFidelityTabIfNeeded() {
    if (!this.fidelityEnabled()) {
      this.tabMenuItems = this.tabMenuItems.filter(x => x.id !== 'FIDELITY');
    }
  }

  private toggleAppointmentTab(appointment: CalendarEvent) {
    if (appointment !== undefined) {
      if (this.isAppointmentTabAlreadyExists()) return;
      this.appointmentDate = appointment.start;
      this.tabMenuItems = [this.appointmentTab, ...this.baseTabMenuItems];
    } else {
      this.tabMenuItems = this.tabMenuItems.filter(t => t.id !== 'APPOINTMENT');
    }
  }

  private isAppointmentTabAlreadyExists() {
    return this.tabMenuItems.find(t => t.id === this.appointmentTab.id);
  }

  protected toggleCustomerInfo() {
    this.customerInfoStore.setIsOpen(!this.customerInfoVisible);
  }

  protected onClose() {
    if (this.needCustomerLogout) {
      this.tunnelDataStore.resetCurrentCustomer();
    }
    this.resetCustomerInfoStore();
    this.appointmentDate = undefined;
  }

  private resetCustomerInfoStore(): void {
    this.customerInfoStore.resetState();
  }

  private loadComponent(tab: CustomMenuItem): void {
    if (!tab) return;
    this.customerInfoContainer.clear();
    this.componentRef = this.customerInfoContainer.createComponent(tab.component);
    if (tab.emitters) {
      tab.emitters.forEach(emitter => {
        this.componentRef.instance[emitter.name].subscribe((data: any) => {
          switch (emitter.type) {
            case EMITTER_TYPE.ON_EDIT:
            case EMITTER_TYPE.ON_DELETE:
              this.onClose();
              break;
          }
        });
      });
    }
    this.componentRef.changeDetectorRef.detectChanges();
  }

  protected readonly faUser = faUser;
  protected readonly faChevronRight = faChevronRight;
  protected readonly FidelityCardTypeEnum = FidelityCardTypeEnum;
}
